import React, { useState, useEffect } from 'react';
import { apiClube } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Wallet, Landmark, Upload, Plus, Trash2, FileSpreadsheet, Lock, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const LivroCaixa = () => {
  // Estados de Autenticação do Clube
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('club_token'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Estados do Livro Caixa
  const [transactions, setTransactions] = useState([]);
  const [activeAccount, setActiveAccount] = useState('caixa1'); // 'caixa1' (Sicredi) ou 'caixa2' (Cofre)
  const [loading, setLoading] = useState(false);

  // Função de Login Isolada da Tesouraria
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const response = await apiClube.auth.login({ email, password });
      localStorage.setItem('club_token', response.data.token);
      setIsAuthenticated(true);
      toast.success("Acesso liberado ao Cofre!");
    } catch (error) {
      console.error(error);
      toast.error("Credenciais inválidas ou sem permissão na Tesouraria.");
    } finally {
      setLoginLoading(false);
    }
  };

  // Função para Trancar o Cofre
  const handleLogout = () => {
    localStorage.removeItem('club_token');
    setIsAuthenticated(false);
    setTransactions([]);
    toast.info("Sessão da Tesouraria encerrada com segurança.");
  };

  const fetchTransactions = async () => {
    if (!isAuthenticated) return; // Trava de segurança para não bater na API sem token
    
    try {
      setLoading(true);
      const response = await apiClube.transactions.getAll(activeAccount);
      setTransactions(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout(); // Se o token expirou, desloga automaticamente
        toast.error("Sua sessão expirou. Faça login novamente.");
      } else {
        toast.error("Erro ao carregar transações");
      }
    } finally {
      setLoading(false);
    }
  };

  // Carrega as transações toda vez que trocar de aba ou logar
  useEffect(() => {
    if (isAuthenticated) {
      fetchTransactions();
    }
  }, [activeAccount, isAuthenticated]);

  const handleDelete = async (id) => {
    if (window.confirm("Remover esta transação?")) {
      try {
        await apiClube.transactions.delete(id);
        toast.success("Removido com sucesso");
        fetchTransactions();
      } catch (error) {
        toast.error("Erro ao remover");
      }
    }
  };

  // Motor de Importação de CSV Dinâmico
  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      toast.loading("Lendo arquivo CSV...", { id: 'import-csv' });

      const lines = text.split(/\r?\n/);
      let successCount = 0;
      let errorCount = 0;

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const cols = line.split(';');
        if (cols.length < 4 || !cols[1].trim()) continue;

        try {
          const rawDate = cols[0].trim();
          const description = cols[1].trim();
          const rawEntrada = cols[2].trim();
          const rawSaida = cols[3].trim();

          const cleanValue = (val) => {
            if (!val || val === '-' || val === '') return 0;
            const cleaned = val.replace(/[R$\s.]/g, '').replace(',', '.');
            return parseFloat(cleaned) || 0;
          };

          const entrada = cleanValue(rawEntrada);
          const saida = cleanValue(rawSaida);

          if (entrada === 0 && saida === 0) continue;

          const type = entrada > 0 ? 'entrada' : 'saida';
          const amount = entrada > 0 ? entrada : saida;

          const [day, month, year] = rawDate.split('/');
          if (!day || !month || !year) throw new Error("Data inválida");
          const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

          const transactionData = {
            account: activeAccount,
            date: formattedDate,
            description: description,
            type: type,
            amount: amount
          };

          await apiClube.transactions.create(transactionData);
          successCount++;

        } catch (err) {
          console.error("Erro na linha " + i + ": ", line, err);
          errorCount++;
        }
      }

      toast.dismiss('import-csv');
      
      if (successCount > 0) {
        toast.success(`${successCount} lançamentos importados para o ${activeAccount === 'caixa1' ? 'Sicredi' : 'Cofre'}!`);
        fetchTransactions();
      } else {
        toast.error("Erro ao importar. Verifique o formato do arquivo.");
      }
      
      e.target.value = null;
    };
    
    reader.readAsText(file); // Ajustado para aceitar codificações variadas (UTF-8/ISO)
  };

  // Se não estiver logado na Tesouraria, renderiza o Modal de Acesso Restrito
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-[70vh] p-4">
        <Card className="w-full max-w-md border-2 border-primary/20 shadow-lg">
          <CardHeader className="text-center space-y-2">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-primary/10 rounded-full">
                <Lock className="w-10 h-10 text-primary" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">Acesso Restrito</CardTitle>
            <p className="text-sm text-muted-foreground">
              A Tesouraria do Cabrones MC possui banco de dados isolado. Insira suas credenciais para gerar a chave de acesso.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">E-mail</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Senha</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  required
                />
              </div>
              <Button type="submit" className="w-full font-bold" disabled={loginLoading}>
                {loginLoading ? 'Autenticando...' : 'Desbloquear Cofre'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // --- CÓDIGO DA TELA PRINCIPAL (SÓ RODA SE ESTIVER LOGADO) ---
  const totalEntradas = transactions.reduce((acc, t) => t.type === 'entrada' ? acc + t.amount : acc, 0);
  const totalSaidas = transactions.reduce((acc, t) => t.type === 'saida' ? acc + t.amount : acc, 0);
  const saldoAtual = totalEntradas - totalSaidas;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight">Livro Caixa - Cabrones MC</h1>
        <div className="flex gap-2 items-center">
          <Button 
            variant={activeAccount === 'caixa1' ? 'default' : 'outline'} 
            onClick={() => setActiveAccount('caixa1')}
          >
            <Landmark className="mr-2 h-4 w-4" /> Caixa 1 (Sicredi)
          </Button>
          <Button 
            variant={activeAccount === 'caixa2' ? 'default' : 'outline'} 
            onClick={() => setActiveAccount('caixa2')}
          >
            <Wallet className="mr-2 h-4 w-4" /> Caixa 2 (Cofre)
          </Button>
          
          {/* Botão de Logout da Tesouraria */}
          <div className="w-px h-8 bg-border mx-2"></div> {/* Separador visual */}
          <Button variant="ghost" className="text-muted-foreground hover:text-destructive" onClick={handleLogout}>
            <LogOut className="mr-2 h-4 w-4" /> Sair
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Saldo {activeAccount === 'caixa1' ? 'Sicredi' : 'Cofre'}</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${saldoAtual >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              R$ {saldoAtual.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-2 border-border bg-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Histórico de Movimentações</CardTitle>
          <div className="flex gap-2">
            <div className="relative">
              <input 
                type="file" 
                accept=".csv" 
                className="hidden" 
                id="import-csv-input" 
                onChange={handleImportCSV}
              />
              <label htmlFor="import-csv-input">
                <Button variant="outline" className="cursor-pointer" asChild>
                  <span><FileSpreadsheet className="mr-2 h-4 w-4" /> Importar Planilha</span>
                </Button>
              </label>
            </div>
            <Button className="bg-primary text-primary-foreground">
              <Plus className="mr-2 h-4 w-4" /> Nova Transação
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead className="text-right">Entrada (R$)</TableHead>
                <TableHead className="text-right">Saída (R$)</TableHead>
                <TableHead className="text-center">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-10">Carregando cofre...</TableCell></TableRow>
              ) : transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                    Nenhuma transação registrada.
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>{new Date(t.date).toLocaleDateString('pt-BR')}</TableCell>
                    <TableCell className="font-medium">{t.description}</TableCell>
                    <TableCell className="text-right text-green-500">
                      {t.type === 'entrada' ? t.amount.toLocaleString('pt-BR', {minimumFractionDigits: 2}) : '-'}
                    </TableCell>
                    <TableCell className="text-right text-red-500">
                      {t.type === 'saida' ? t.amount.toLocaleString('pt-BR', {minimumFractionDigits: 2}) : '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(t.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

const DollarSign = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>
);

export default LivroCaixa;
